// Presentation layer for session reminders (EPIC E7). The wire vocabulary is
// frozen lowercase-hyphen strings owned by the BE; the labels and colours are
// owned here, by the admin app. Both the fleet dashboard and the per-session
// audience grid import from this file so a state can never be amber in one
// place and red in the other.
//
// THE RULE FOR EVERY MAP BELOW: an unrecognised value is rendered verbatim,
// never dropped and never crashed on. The BE is allowed to widen its enums
// without waiting for an admin release.

import {
  OFFSET_CANCELLED,
  OFFSET_MANUAL,
  OFFSET_MOVED,
  REMINDER_ALERT_REASONS,
} from 'src/@types/reminder';

type ChipColor = 'default' | 'info' | 'success' | 'warning' | 'error';
type ChipVariant = 'filled' | 'outlined' | 'ghost';

export type StateChipStyle = { color: ChipColor; variant: ChipVariant };

// The five stored states plus the two the audience grid computes. The computed
// pair is deliberately OUTLINED: an outline reads as "not yet real", which is
// exactly what `planned` and `would-suppress` are — nothing has been written.
const STATE_STYLE: Record<string, StateChipStyle> = {
  claimed: { color: 'default', variant: 'ghost' },
  sending: { color: 'info', variant: 'ghost' },
  sent: { color: 'success', variant: 'ghost' },
  failed: { color: 'error', variant: 'ghost' },
  suppressed: { color: 'warning', variant: 'ghost' },
  planned: { color: 'default', variant: 'outlined' },
  'would-suppress': { color: 'warning', variant: 'outlined' },
};

export const stateStyle = (state: string): StateChipStyle =>
  STATE_STYLE[state] || { color: 'default', variant: 'outlined' };

const STATE_LABEL: Record<string, string> = {
  claimed: 'Claimed',
  sending: 'Sending',
  sent: 'Sent',
  failed: 'Failed',
  suppressed: 'Suppressed',
  planned: 'Planned',
  'would-suppress': 'Would suppress',
};

export const stateLabel = (state: string): string => STATE_LABEL[state] || state;

// Reasons are title-cased from the hyphenated wire value, so a reason the BE
// adds tomorrow ("carrier-blocked") still reads as "Carrier blocked" today.
// Only the handful whose plain title-case would be cryptic get an entry.
const REASON_LABEL: Record<string, string> = {
  'window-lapsed': 'Window lapsed',
  'unknown-outcome': 'Unknown outcome',
  'created-after-window': 'Session created after the window',
  'enrolled-after-window': 'Learner enrolled after the window',
  'rescheduled-after-window': 'Moved past the window',
  'minor-reschedule': 'Minor reschedule',
  'session-missing': 'Session missing',
  'too-late': 'Class already started',
  'opted-out': 'Opted out',
  'quiet-hours': 'Quiet hours',
  'no-email': 'No email on record',
  'no-phone': 'No phone on record',
  'invalid-phone': 'Invalid phone',
  'channel-unconfigured': 'Channel unconfigured',
  'channel-disabled': 'Channel disabled',
  'provider-rejected': 'Provider rejected',
  'provider-error': 'Provider error',
  'idempotent-replay': 'Already delivered (replay)',
  'batch-paused': 'Cohort paused',
  'sends-paused': 'Sends paused',
};

export const reasonLabel = (reason?: string | null): string => {
  if (!reason) return '—';
  if (REASON_LABEL[reason]) return REASON_LABEL[reason];
  const spaced = reason.replace(/-/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

// One line of plain English per reason, shown as the row tooltip. This is the
// whole point of the epic: "why didn't it go?" answered from a stored fact.
const REASON_HELP: Record<string, string> = {
  'window-lapsed': 'The moment passed unfired — the scheduler was down or backed up.',
  abandoned: 'Claimed but never executed before its window closed. Nothing went out.',
  'unknown-outcome':
    'Dispatched, outcome unconfirmed. It may already have been delivered — resending could send it twice.',
  'created-after-window': 'The session was created after this reminder’s moment. Expected.',
  'enrolled-after-window': 'The learner enrolled after this reminder’s moment. Expected.',
  'rescheduled-after-window': 'The class was moved to a time after this reminder’s moment.',
  cancelled: 'The class was cancelled before this reminder fired.',
  rescheduled: 'The class no longer has this start time.',
  'minor-reschedule':
    'The class shifted by under 15 minutes; a reminder for the near-identical time already went out.',
  'session-missing': 'No session exists at that occurrence any more.',
  superseded: 'A later schedule change replaced this notice.',
  'too-late': 'The class had already started when the send ran.',
  'opted-out': 'The learner turned this channel off. It cannot be resent.',
  'quiet-hours': 'It was night for the learner and the class is not a night class. WhatsApp only.',
  'no-email': 'The learner record has no email address.',
  'no-phone': 'The learner record has no phone number.',
  'invalid-phone': 'The phone number is not E.164-normalisable — fix the record.',
  'channel-unconfigured': 'The provider credentials were blank when this was claimed.',
  'channel-disabled': 'This channel is switched off fleet-wide.',
  'provider-rejected': 'The provider reached us and said no. A resend is safe.',
  'provider-error': 'A transport or timeout error before dispatch.',
  'idempotent-replay': 'The in-app notification already existed — counted as delivered.',
  'batch-paused':
    'Live classes for this cohort were paused (Batch Management), so this reminder was held back on purpose. Nothing went out.',
  'sends-paused':
    'Reminder sends were paused from this page, so this was held back on purpose. Nothing went out.',
};

export const reasonHelp = (reason?: string | null): string =>
  (reason && REASON_HELP[reason]) || '';

export const isAlertReason = (reason?: string | null): boolean =>
  !!reason && REMINDER_ALERT_REASONS.includes(reason);

// Offsets. The three sentinels are NOT reminders and must never be shown as
// "-1 minutes before" — they are the cancel/move notices and the operator
// resend, and an operator reading the table has to be able to tell them apart.
const OFFSET_LABEL: Record<number, string> = {
  1440: '24 hours before',
  60: '1 hour before',
  10: '10 minutes before',
  [OFFSET_CANCELLED]: 'Cancelled notice',
  [OFFSET_MOVED]: 'Moved notice',
  [OFFSET_MANUAL]: 'Manual resend',
};

export const offsetLabel = (offset: number): string =>
  OFFSET_LABEL[offset] || `${offset} min`;

// The compact form for a narrow table cell or a column header.
const OFFSET_SHORT: Record<number, string> = {
  1440: '24 h',
  60: '1 h',
  10: '10 min',
  [OFFSET_CANCELLED]: 'Cancel',
  [OFFSET_MOVED]: 'Moved',
  [OFFSET_MANUAL]: 'Resend',
};

export const offsetShort = (offset: number): string =>
  OFFSET_SHORT[offset] || String(offset);

export const isNotice = (offset: number): boolean => offset < 0;

const CHANNEL_LABEL: Record<string, string> = {
  inapp: 'In-app',
  email: 'Email',
  whatsapp: 'WhatsApp',
};

export const channelLabel = (channel: string): string =>
  CHANNEL_LABEL[channel] || channel;

const CHANNEL_ICON: Record<string, string> = {
  inapp: 'eva:bell-fill',
  email: 'eva:email-fill',
  whatsapp: 'eva:message-circle-fill',
};

export const channelIcon = (channel: string): string =>
  CHANNEL_ICON[channel] || 'eva:radio-button-off-outline';

// Where the row's timezone came from (§9.5). Shown as a caption so ops can see
// at a glance whether a learner has actually set one.
const TZ_SOURCE_LABEL: Record<string, string> = {
  user: 'learner’s own',
  batch: 'batch default',
  default: 'platform default',
};

export const tzSourceLabel = (source: string): string =>
  TZ_SOURCE_LABEL[source] || source;

// ----------------------------------------------------------------------
// Error rendering.
//
// The axios interceptor rejects with `error.response.data` — the BE's global
// filters emit { error, message } or { error, messages[] }, NOT Nest defaults.
// `error` is the exception class name, which is the only way the status code
// survives the interceptor; that is what tells a 409 (a normal, expected
// outcome of the once-ever resend rule) from a real failure.

export const apiMessage = (err: any, fallback = 'Something went wrong'): string => {
  if (typeof err === 'string') return err || fallback;
  return err?.message || err?.messages?.[0] || fallback;
};

export const isConflict = (err: any): boolean =>
  typeof err === 'object' && err !== null && err.error === 'ConflictException';
